import React, { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { format } from 'date-fns';
import { TreeItemType, TreeMenu } from 'src/presentations/components/molecules/treeMenu';

interface TransactionDateMenuProps {
  onClose: () => void;
  onApply?: (selectedDates: string[]) => void;
  isVisible?: boolean;
  onToggle?: () => void;
  toggleButtonRef?: React.RefObject<HTMLButtonElement>;
  availableTransactionDates?: string[]; // Transaction dates from metadata API
  isLoading?: boolean; // Loading state from metadata API
}

export const TransactionDateMenu: FC<TransactionDateMenuProps> = ({
  onClose,
  onApply,
  onToggle,
  isVisible = false,
  toggleButtonRef,
  availableTransactionDates = [],
  isLoading = false,
}): JSX.Element => {
  const { setValue, watch } = useFormContext();
  const selectedDates = watch('transactionDate') || [];
  const billedEntityId = watch('billedEntityId');

  // Track if we've already set the initial transaction date for this entity
  const hasSetInitialDate = useRef<boolean>(false);
  const currentEntityId = useRef<string>(billedEntityId);

  // Helper function to get current month in YYYY-MM format
  const getCurrentMonth = useCallback(() => {
    const now = new Date();
    return format(now, 'yyyy-MM');
  }, []);

  // Helper function to get quarter from month
  const getQuarter = (month: number): { quarter: number; quarterLabel: string } => {
    if (month >= 1 && month <= 3) return { quarter: 1, quarterLabel: 'Q1' };
    if (month >= 4 && month <= 6) return { quarter: 2, quarterLabel: 'Q2' };
    if (month >= 7 && month <= 9) return { quarter: 3, quarterLabel: 'Q3' };
    return { quarter: 4, quarterLabel: 'Q4' };
  };

  // Process available transaction dates into tree structure
  const { treeItems, availableMonths } = useMemo(() => {
    if (!availableTransactionDates || availableTransactionDates.length === 0) {
      return { treeItems: [], availableMonths: [] };
    }

    // Group dates by year, quarter, and month
    const dateGroups: Record<string, Record<string, Record<string, string[]>>> = {};
    const months: string[] = [];

    availableTransactionDates.forEach((dateStr) => {
      // Handle different date formats: MM-YYYY format from API
      let year: string;
      let month: number;

      if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        if (parts.length === 2) {
          // MM-YYYY format
          month = parseInt(parts[0]);
          year = parts[1];
        } else {
          // YYYY-MM format
          year = parts[0];
          month = parseInt(parts[1]);
        }
      } else {
        // Skip invalid formats
        return;
      }

      const { quarterLabel } = getQuarter(month);
      const monthLabel = new Date(parseInt(year), month - 1).toLocaleDateString('en-US', { month: 'long' });

      if (!dateGroups[year]) {
        dateGroups[year] = {};
      }
      if (!dateGroups[year][quarterLabel]) {
        dateGroups[year][quarterLabel] = {};
      }
      if (!dateGroups[year][quarterLabel][monthLabel]) {
        dateGroups[year][quarterLabel][monthLabel] = [];
      }

      // Store the month value for selection in YYYY-MM format
      const monthValue = `${year}-${String(month).padStart(2, '0')}`;
      if (!dateGroups[year][quarterLabel][monthLabel].includes(monthValue)) {
        dateGroups[year][quarterLabel][monthLabel].push(monthValue);
        months.push(monthValue);
      }
    });

    // Convert to tree structure
    const items: TreeItemType<string>[] = [];

    Object.keys(dateGroups)
      .sort((a, b) => parseInt(b) - parseInt(a)) // Most recent year first
      .forEach((year) => {
        const quarterItems: TreeItemType<string>[] = [];

        Object.keys(dateGroups[year])
          .sort((a, b) => {
            const quarterOrder = ['Q4', 'Q3', 'Q2', 'Q1'];
            return quarterOrder.indexOf(a) - quarterOrder.indexOf(b); // Most recent quarter first
          })
          .forEach((quarter) => {
            const quarterValue = `${year}-${quarter}`;
            const monthItems: TreeItemType<string>[] = [];

            Object.keys(dateGroups[year][quarter])
              .sort((a, b) => {
                const monthOrder = ['December', 'November', 'October', 'September', 'August', 'July', 'June', 'May', 'April', 'March', 'February', 'January'];
                return monthOrder.indexOf(a) - monthOrder.indexOf(b); // Most recent month first
              })
              .forEach((month) => {
                // Use the first month value for this month (there should only be one per month)
                const monthValue = dateGroups[year][quarter][month][0];

                monthItems.push({
                  id: `${year}-${quarter}-${month}`,
                  label: month,
                  value: monthValue,
                  selected: false, // Will be updated based on selectedDates
                  metadata: {
                    statusColor: null,
                  },
                });
              });

            quarterItems.push({
              id: quarterValue,
              label: quarter,
              value: quarterValue,
              selected: false, // Will be updated based on selectedDates
              subItems: monthItems,
              metadata: {
                statusColor: null,
              },
            });
          });

        items.push({
          id: year,
          label: year,
          value: year,
          selected: false, // Will be updated based on selectedDates
          subItems: quarterItems,
          metadata: {
            statusColor: null,
          },
        });
      });

    return { treeItems: items, availableMonths: months };
  }, [availableTransactionDates]);

  // Update selection state in tree items based on current selectedDates
  const treeItemsWithSelection = useMemo(() => {
    const updateSelectionState = (items: TreeItemType<string>[]): TreeItemType<string>[] => {
      return items.map((item) => {
        const updatedItem = { ...item };

        if (item.subItems) {
          // First update all sub-items
          updatedItem.subItems = updateSelectionState(item.subItems);

          // For parent items (quarters and years), check if they are directly selected
          // OR if all their children are selected
          const isDirectlySelected = selectedDates.includes(item.value);
          const areAllChildrenSelected = updatedItem.subItems.every((subItem) => subItem.selected) && updatedItem.subItems.length > 0;

          updatedItem.selected = isDirectlySelected || areAllChildrenSelected;
        } else {
          // For leaf items (months), check multiple conditions:
          // 1. The month itself is selected
          // 2. The quarter containing this month is selected
          // 3. The year containing this month is selected
          const monthValue = item.value; // e.g., "2024-10"
          const [year, monthStr] = monthValue.split('-');
          const monthNum = parseInt(monthStr);
          const { quarter } = getQuarter(monthNum);
          const quarterValue = `${year}-Q${quarter}`;
          const yearValue = year;

          updatedItem.selected = selectedDates.includes(monthValue) || selectedDates.includes(quarterValue) || selectedDates.includes(yearValue);
        }

        return updatedItem;
      });
    };

    return updateSelectionState(treeItems);
  }, [treeItems, selectedDates]);

  // Effect to clear transaction dates and reset flag when billed entity changes
  useEffect(() => {
    if (billedEntityId !== currentEntityId.current) {
      // Billed entity changed — set null to signal "not initialized yet"
      setValue('transactionDate', null);
      hasSetInitialDate.current = false;
      currentEntityId.current = billedEntityId;
    }
  }, [billedEntityId, setValue]);

  // Effect to set initial transaction date only once per entity
  useEffect(() => {
    if (!billedEntityId || hasSetInitialDate.current) return;

    if (availableMonths.length > 0) {
      const currentMonth = getCurrentMonth();

      // Smart auto-selection: prefer current month, otherwise select most recent available
      let selectedMonth: string;
      if (availableMonths.includes(currentMonth)) {
        // Current month has transactions - use it
        selectedMonth = currentMonth;
      } else {
        // Current month not available - select most recent available month
        // Sort months in descending order and take the first (most recent)
        const sortedMonths = [...availableMonths].sort((a, b) => b.localeCompare(a));
        selectedMonth = sortedMonths[0];
      }

      setValue('transactionDate', [selectedMonth]);
      hasSetInitialDate.current = true;
    } else if (!isLoading) {
      // Metadata loaded but no available months — set empty array to unblock the query
      setValue('transactionDate', []);
      hasSetInitialDate.current = true;
    }
  }, [billedEntityId, availableMonths, isLoading, setValue, getCurrentMonth]);

  const handleApply = useCallback(
    (selectedValues: string[]) => {
      setValue('transactionDate', selectedValues);

      if (onApply) {
        onApply(selectedValues);
      }
    },
    [setValue, onApply]
  );

  // Tree menu configuration
  const config = {
    title: 'Showing Transaction',
    searchPlaceholder: 'Search',
    width: '380px',
    height: '600px',
    maxHeight: '80vh',
    showSearch: true,
    showSelectAll: true,
    showClear: true,
  };

  return (
    <TreeMenu
      items={treeItemsWithSelection}
      config={config}
      isVisible={isVisible}
      isLoading={isLoading}
      selectedValues={selectedDates}
      onClose={onClose}
      onApply={handleApply}
      onToggle={onToggle}
      toggleButtonRef={toggleButtonRef}
    />
  );
};
